class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    // Cycle 60: Clarity event for the drawer CHECKOUT button. Delegated, so it
    // survives the drawer's section re-renders; it never stops the submit.
    this.addEventListener('click', (evt) => {
      if (window.wcTrack && evt.target && evt.target.closest && evt.target.closest('#CartDrawer-Checkout')) window.wcTrack('wc_checkout_click');
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Cycle 60: Clarity events. wc_drawer_open only on closed -> open; the offer
    // progress (eligible tees, capped at 6) every time open() runs, which is after
    // each add re-renders the drawer.
    // Review 2026-10-31: no Clarity events from the instant (pending) open; it
    // would log a stale offer count, and a failed add would count as an open.
    if (window.wcTrack && !this.classList.contains('wc-is-pending')) {
      if (!this.classList.contains('active')) window.wcTrack('wc_drawer_open');
      const wcOfferLine = this.querySelector('.wc-offer-cart[data-wc-offer-n]');
      const wcOfferN = wcOfferLine ? parseInt(wcOfferLine.getAttribute('data-wc-offer-n'), 10) : 0;
      if (wcOfferN > 0) window.wcTrack('wc_offer_progress_' + Math.min(wcOfferN, 6));
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // 2026-10-31: the bag now opens the instant Add to Bag is tapped (showPending),
    // then renderContents calls open() again on an already-open drawer. No slide
    // transition runs then, so trap focus now instead of on a transitionend that
    // would only fire on the next close.
    const wcAlreadyOpen = this.classList.contains('active');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    const wcTrap = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
    };
    if (wcAlreadyOpen) setTimeout(wcTrap);
    else this.addEventListener('transitionend', wcTrap, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // 2026-10-31: instant bag. Opens the drawer the moment an add starts, with a
  // pending row (photo, name, size, "Adding to your bag...") on top of whatever
  // the bag already shows. renderContents swaps #CartDrawer, which removes the
  // row; clearPending() also runs there and on any failed add.
  showPending(info) {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;
    let row = this.querySelector('.wc-cart-pending');
    if (!row) {
      row = document.createElement('div');
      row.className = 'wc-cart-pending';
      row.setAttribute('role', 'status');
      row.setAttribute('aria-live', 'polite');
      const head = inner.querySelector('.wc-cart-header');
      if (head) head.after(row);
      else inner.prepend(row);
    }
    row.innerHTML =
      '<div class="wc-cart-pending__img"></div>' +
      '<div class="wc-cart-pending__copy"><p class="wc-cart-pending__title"></p><p class="wc-cart-pending__meta"></p></div>' +
      '<span class="wc-cart-pending__spin" aria-hidden="true"></span>';
    if (info && info.image) {
      const img = document.createElement('img');
      img.src = info.image;
      img.alt = '';
      row.querySelector('.wc-cart-pending__img').appendChild(img);
    }
    row.querySelector('.wc-cart-pending__title').textContent = (info && info.title) || 'Your shirt';
    row.querySelector('.wc-cart-pending__meta').textContent =
      (info && info.size ? info.size + ' \u00b7 ' : '') + 'Adding to your bag\u2026';
    this.wcPendingOpened = !this.classList.contains('active');
    // Review 2026-10-31: the bag slides over the Add to Bag button within a few
    // ms, so a double tap used to land on Checkout or a recommendation. Ignore
    // taps on the bag while pending and for a moment after (see clearPending).
    clearTimeout(this.wcGuardTimer);
    this.classList.add('wc-is-pending', 'wc-tap-guard');
    // Hidden empty-bag links stay focusable for Dawn's trap; make them inert.
    const emptyBlock = this.querySelector('.wc-cart-empty');
    if (emptyBlock) emptyBlock.setAttribute('inert', '');
    this.open();
  }

  clearPending(failed, message) {
    const row = this.querySelector('.wc-cart-pending');
    const emptyBlock = this.querySelector('.wc-cart-empty');
    if (emptyBlock) emptyBlock.removeAttribute('inert');
    const openedByPending = this.wcPendingOpened;
    this.wcPendingOpened = false;
    clearTimeout(this.wcGuardTimer);
    if (failed && row) {
      // Say what happened for a moment instead of flashing the bag shut.
      row.classList.add('wc-cart-pending--error');
      const meta = row.querySelector('.wc-cart-pending__meta');
      if (meta) meta.textContent = message || "Couldn't add that one. Please try again.";
      const spin = row.querySelector('.wc-cart-pending__spin');
      if (spin) spin.remove();
      this.wcGuardTimer = setTimeout(() => {
        row.remove();
        this.classList.remove('wc-is-pending', 'wc-tap-guard');
        if (openedByPending) this.close();
      }, 1500);
      return;
    }
    if (row) row.remove();
    this.classList.remove('wc-is-pending');
    if (failed && openedByPending) this.close();
    if (!failed && openedByPending && window.wcTrack) window.wcTrack('wc_drawer_open');
    this.wcGuardTimer = setTimeout(() => this.classList.remove('wc-tap-guard'), 450);
  }

  renderContents(parsedState) {
    this.clearPending();
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
